import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link, useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
} from "@mui/material";
import { format } from "date-fns";
import {
  fetchAllOrganizations,
  crmSliceSelector,
} from "../../../redux/features/crmSlice";

export default function CRMOrganizations() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const crmState = useSelector((state) => crmSliceSelector(state));
  const {
    organizations,
    organizationsLoading,
    organizationsFetched,
    usersMap,
  } = crmState;

  const getUserDisplayName = (userId) => {
    const user = usersMap[userId];
    if (!user) return userId;
    return user.displayName || user.email || userId;
  };

  useEffect(() => {
    // Refresh data when navigating to this page
    dispatch(fetchAllOrganizations());
  }, [location.pathname, dispatch]);

  return (
    <Container>
      <Typography variant="h2" paddingTop={"20px"}>
        Organizations
      </Typography>

      {organizationsLoading && (
        <LinearProgress sx={{ marginTop: 2, marginBottom: 2 }} />
      )}

      {!organizationsLoading && organizationsFetched && (
        <Box marginTop={4}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Admins</TableCell>
                  <TableCell>Scanners</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organizations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No organizations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  organizations.map((org) => {
                    const {
                      id,
                      name,
                      owner,
                      admins = [],
                      scanners = [],
                      createdAt,
                    } = org;
                    const createdDate = createdAt?.toDate
                      ? format(createdAt.toDate(), "MMM d, yyyy")
                      : "N/A";

                    return (
                      <TableRow key={id}>
                        <TableCell>
                          <Typography
                            variant="body1"
                            fontWeight="medium"
                            sx={{
                              cursor: "pointer",
                              color: "primary.main",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                            onClick={() => navigate(`/crm/organizations/${id}`)}
                          >
                            {name || "Unnamed Organization"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {owner ? (
                            <Typography
                              variant="body2"
                              component={Link}
                              to={`/crm/users/${owner}`}
                              sx={{
                                textDecoration: "none",
                                color: "primary.main",
                              }}
                            >
                              {getUserDisplayName(owner)}
                            </Typography>
                          ) : (
                            <Typography variant="body2">N/A</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {admins.length > 0 ? (
                            <Box>
                              {admins.map((adminId, idx) => (
                                <React.Fragment key={adminId}>
                                  <Typography
                                    variant="body2"
                                    component={Link}
                                    to={`/crm/users/${adminId}`}
                                    sx={{
                                      textDecoration: "none",
                                      color: "primary.main",
                                    }}
                                  >
                                    {getUserDisplayName(adminId)}
                                  </Typography>
                                  {idx < admins.length - 1 && (
                                    <Typography
                                      variant="body2"
                                      component="span"
                                    >
                                      {", "}
                                    </Typography>
                                  )}
                                </React.Fragment>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2">None</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {scanners.length > 0 ? (
                            <Box>
                              {scanners.map((scannerId, idx) => (
                                <React.Fragment key={scannerId}>
                                  <Typography
                                    variant="body2"
                                    component={Link}
                                    to={`/crm/users/${scannerId}`}
                                    sx={{
                                      textDecoration: "none",
                                      color: "primary.main",
                                    }}
                                  >
                                    {getUserDisplayName(scannerId)}
                                  </Typography>
                                  {idx < scanners.length - 1 && (
                                    <Typography
                                      variant="body2"
                                      component="span"
                                    >
                                      {", "}
                                    </Typography>
                                  )}
                                </React.Fragment>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="body2">None</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{createdDate}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Container>
  );
}
